import { Controller } from "@hotwired/stimulus"
import { storeFile, getFile, deleteFile } from "../lib/offline_storage"

interface QueuedFile {
  fileId: string
  fileName: string
  fileType: string
  fieldName: string
}

interface QueuedRequest {
  url: string
  method: string
  body: Record<string, string>
  timestamp: number
  files: QueuedFile[]
  attempts: number
}

export default class OfflineQueueController extends Controller {
  static targets = ["count", "pending", "status", "error"]
  static values = { queued: Number, syncStatus: String }

  declare readonly hasCountTarget: boolean
  declare readonly countTarget: HTMLElement
  declare readonly hasPendingTarget: boolean
  declare readonly pendingTarget: HTMLElement
  declare readonly hasStatusTarget: boolean
  declare readonly statusTarget: HTMLElement
  declare readonly hasErrorTarget: boolean
  declare readonly errorTarget: HTMLElement
  declare queuedValue: number
  declare syncStatusValue: string

  private storageKey = "offline-form-queue"
  private readonly maxAttempts = 5
  private syncing = false

  connect(): void {
    this.queuedValue = this.getQueue().length
    this.syncStatusValue = "idle"
    this.updateCount()
    this.updateSyncStatus()

    document.addEventListener("turbo:submit-start", this.handleSubmitStart)
    window.addEventListener(
      "offline:status-change",
      this.handleStatusChange as EventListener,
    )
  }

  disconnect(): void {
    document.removeEventListener("turbo:submit-start", this.handleSubmitStart)
    window.removeEventListener(
      "offline:status-change",
      this.handleStatusChange as EventListener,
    )
  }

  private handleSubmitStart = async (event: Event): Promise<void> => {
    if (navigator.onLine) return

    const turboEvent = event as any
    const formSubmission = turboEvent.detail?.formSubmission
    if (!formSubmission) return

    const form = formSubmission.formElement as HTMLFormElement
    if (!form) return

    event.preventDefault()
    event.stopImmediatePropagation()

    const body: Record<string, string> = {}
    const files: QueuedFile[] = []
    const formData = new FormData(form)
    const storePromises: Promise<void>[] = []

    formData.forEach((value, key) => {
      if (value instanceof File) {
        const fileId = crypto.randomUUID()
        files.push({
          fileId,
          fileName: value.name,
          fileType: value.type,
          fieldName: key,
        })
        storePromises.push(storeFile(fileId, value))
      } else {
        body[key] = value as string
      }
    })

    try {
      await Promise.all(storePromises)
    } catch {
      this.warnStorageFull()
      Promise.allSettled(files.map(f => deleteFile(f.fileId)))
      return
    }

    this.enqueue({
      url: form.action,
      method: form.method.toUpperCase() || "POST",
      body,
      timestamp: Date.now(),
      attempts: 0,
      files,
    })
  }

  private handleStatusChange = (event: Event): void => {
    const detail = (event as CustomEvent).detail
    if (detail.online) {
      this.replayQueue()
    }
  }

  private enqueue(request: QueuedRequest): void {
    const queue = this.getQueue()
    queue.push(request)
    localStorage.setItem(this.storageKey, JSON.stringify(queue))
    this.queuedValue = queue.length
    this.updateCount()
  }

  private async replayQueue(): Promise<void> {
    if (this.syncing) return

    const queue = this.getQueue()
    if (queue.length === 0) return

    this.syncing = true
    this.syncStatusValue = "syncing"
    this.updateSyncStatus()

    const remaining: QueuedRequest[] = []

    try {
      for (const item of queue) {
        const files = this.resolveFiles(item)
        let response: Response

        if (files.length > 0) {
          const body = new FormData()
          Object.entries(item.body).forEach(([key, value]) => {
            body.append(key, value)
          })

          let allFilesFound = true
          for (const f of files) {
            const file = await getFile(f.fileId)
            if (!file) {
              allFilesFound = false
              break
            }
            body.append(f.fieldName, file, f.fileName || "photo.jpg")
          }

          if (!allFilesFound) {
            remaining.push(item)
            continue
          }

          response = await fetch(item.url, {
            method: item.method,
            headers: {
              "Accept": "text/vnd.turbo-stream.html, text/html",
              "X-CSRF-Token": this.getCsrfToken(),
            },
            body,
          })

          if (response.ok) {
            await Promise.all(files.map(f => deleteFile(f.fileId)))
          }
        } else {
          const body = new URLSearchParams()
          Object.entries(item.body).forEach(([key, value]) => {
            body.append(key, value)
          })

          response = await fetch(item.url, {
            method: item.method,
            headers: {
              "Accept": "text/vnd.turbo-stream.html, text/html",
              "Content-Type": "application/x-www-form-urlencoded",
              "X-CSRF-Token": this.getCsrfToken(),
            },
            body,
          })
        }

        if (response.ok) {
          const html = await response.text()
          const Turbo = (window as any).Turbo
          if (Turbo && html.includes("turbo-stream")) {
            Turbo.renderStreamMessage(html)
          }
        } else {
          const nextAttempts = (item.attempts ?? 0) + 1
          if (nextAttempts < this.maxAttempts) {
            remaining.push({ ...item, attempts: nextAttempts })
          } else {
            await Promise.all(files.map(f => deleteFile(f.fileId)))
          }
        }
      }

      localStorage.setItem(this.storageKey, JSON.stringify(remaining))
      this.queuedValue = remaining.length
      this.updateCount()
    } finally {
      this.syncing = false
      this.syncStatusValue = remaining.length > 0 ? "error" : "idle"
      this.updateSyncStatus()
    }
  }

  private resolveFiles(item: QueuedRequest): QueuedFile[] {
    if (item.files && item.files.length > 0) return item.files

    // Backward compat: old format stored single file fields directly on the item
    const old = item as unknown as Record<string, unknown>
    if (old.fileId) {
      return [{
        fileId: old.fileId as string,
        fileName: (old.fileName as string) || "photo.jpg",
        fileType: (old.fileType as string) || "",
        fieldName: "photo",
      }]
    }

    return []
  }

  private getQueue(): QueuedRequest[] {
    try {
      const data = localStorage.getItem(this.storageKey)
      return data ? JSON.parse(data) : []
    } catch {
      return []
    }
  }

  private getCsrfToken(): string {
    const meta = document.querySelector('meta[name="csrf-token"]')
    if (!meta) return ""

    return (meta as HTMLMetaElement).content
  }

  private updateCount(): void {
    if (this.hasCountTarget) {
      this.countTarget.textContent = String(this.queuedValue)
      if (this.queuedValue > 0) {
        this.countTarget.classList.remove("hidden")
      } else {
        this.countTarget.classList.add("hidden")
      }
    }

    const photoCount = this.getQueue().reduce((sum, item) => {
      return sum + this.resolveFiles(item).length
    }, 0)
    if (this.hasPendingTarget && photoCount > 0) {
      this.pendingTarget.textContent = String(photoCount)
      this.pendingTarget.classList.remove("hidden")
    } else if (this.hasPendingTarget) {
      this.pendingTarget.classList.add("hidden")
    }
  }

  private updateSyncStatus(): void {
    if (this.hasStatusTarget) {
      this.statusTarget.setAttribute("data-sync-status", this.syncStatusValue)
    }
    if (this.hasErrorTarget) {
      if (this.syncStatusValue === "error") {
        this.errorTarget.classList.remove("hidden")
      } else {
        this.errorTarget.classList.add("hidden")
      }
    }
  }

  private warnStorageFull(): void {
    this.syncStatusValue = "error"
    this.updateSyncStatus()
    window.setTimeout(() => {
      this.syncStatusValue = "idle"
      this.updateSyncStatus()
    }, 5000)
  }
}
